import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@g17eco/ui-modern/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import { Loader2 } from 'lucide-react';
import { useGetMonthlyByEntityQuery, useGetAssetsQuery } from '../../api/emissions';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { convertEmissionValue } from '../../utils/emissionsFormatter';
import type { AssetType } from '../../types/emissions';

const ENTITY_COLORS = [
  '#84cc16', '#10b981', '#0ea5e9', '#8b5cf6', '#f43f5e',
  '#f97316', '#06b6d4', '#a855f7', '#ec4899', '#14b8a6',
];

const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface EntityTrendChartProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  groupBy: 'initiative' | 'asset';
  includeChildren?: boolean;
  assetType?: AssetType | 'unassigned';
}

export const EntityTrendChart = ({ initiativeId, periodStart, periodEnd, groupBy, includeChildren, assetType }: EntityTrendChartProps) => {
  const { settings } = useEmissionsFormatter();
  const { data, isLoading } = useGetMonthlyByEntityQuery(
    { initiativeId, periodStart, periodEnd, groupBy },
    { skip: !initiativeId }
  );

  // For asset grouping, fetch asset names to resolve entityId → name
  const { data: assetsResponse } = useGetAssetsQuery(
    { initiativeId, limit: 100, includeChildren },
    { skip: !initiativeId || groupBy !== 'asset' }
  );
  const assetMap = useMemo(() => {
    if (!assetsResponse?.data) return new Map<string, string>();
    return new Map(assetsResponse.data.map((a) => [a._id, a.name]));
  }, [assetsResponse?.data]);

  const { chartData, entityNames, chartConfig } = useMemo(() => {
    if (!data?.results?.length) return { chartData: [], entityNames: [] as string[], chartConfig: {} as ChartConfig };

    // Get unique entities
    const entitySet = new Map<string, string>();
    for (const row of data.results) {
      if (row.entityId === 'unassigned') {
        entitySet.set(row.entityId, 'Unassigned');
        continue;
      }
      const name = groupBy === 'asset'
        ? (assetMap.get(row.entityId) ?? row.entityName ?? row.entityId)
        : (row.entityName ?? row.entityId);
      entitySet.set(row.entityId, name);
    }

    // Filter by asset type if specified (only for asset groupBy)
    let filteredEntityIds: Set<string> | null = null;
    if (groupBy === 'asset' && assetType && assetsResponse?.data) {
      if (assetType === 'unassigned') {
        filteredEntityIds = new Set(['unassigned']);
      } else {
        filteredEntityIds = new Set(
          assetsResponse.data.filter((a) => a.assetType === assetType).map((a) => a._id)
        );
      }
    }

    const entities = Array.from(entitySet.entries())
      .filter(([id]) => !filteredEntityIds || filteredEntityIds.has(id));

    // Build time-series: { month: 'Jan 26', [entityId]: value, ... }
    const timeMap = new Map<string, Record<string, string | number>>();
    for (const row of data.results) {
      if (filteredEntityIds && !filteredEntityIds.has(row.entityId)) continue;
      const key = `${row.year}-${String(row.month).padStart(2, '0')}`;
      const label = `${monthNames[row.month - 1]} '${String(row.year).slice(2)}`;
      if (!timeMap.has(key)) {
        timeMap.set(key, { month: label });
      }
      const point = timeMap.get(key)!;
      point[row.entityId] = convertEmissionValue(row.total, settings.displayUnit);
    }

    const sorted = Array.from(timeMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v);

    const config: ChartConfig = {};
    entities.forEach(([id, name], i) => {
      config[id] = { label: name, color: ENTITY_COLORS[i % ENTITY_COLORS.length] };
    });

    return {
      chartData: sorted,
      entityNames: entities.map(([id]) => id),
      chartConfig: config,
    };
  }, [data, assetMap, assetType, assetsResponse?.data, groupBy, settings.displayUnit]);

  const title = groupBy === 'initiative' ? 'Emissions by Reporting Level' : 'Emissions by Asset';

  if (isLoading) {
    return (
      <Card>
        <CardContent className='tw:flex tw:items-center tw:justify-center tw:py-16'>
          <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='tw:text-sm tw:text-muted-foreground tw:text-center tw:py-12'>No trend data available for this period.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>Monthly emissions trend per {groupBy === 'initiative' ? 'reporting level' : 'asset'}</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className='tw:h-[350px] tw:w-full'>
          <LineChart data={chartData}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey='month' tickLine={false} axisLine={false} tickMargin={8} tick={{ fontSize: 10 }} />
            <YAxis tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v: number) => v.toLocaleString()} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            {entityNames.map((id) => (
              <Line
                key={id}
                type='monotone'
                dataKey={id}
                name={String(chartConfig[id]?.label ?? id)}
                stroke={`var(--color-${id})`}
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            ))}
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
